import type { TenantKind } from './Enums';

/**
 * Tenants are external parties (persons or companies) that rent something,
 * stored at `tenants/{tenantKey}`. They are not app users, so the UID-only
 * rule does not apply to them; GDPR does: name, contact details and org
 * number only, never a personal identity number. Schema shared with
 * mf-functions rules — see docs/features/parking-tenants/03-plan.md (D2).
 */
export interface TenantData {
    kind: TenantKind;
    name: string;
    /** Companies only. */
    orgNumber?: string;
    email?: string;
    phone?: string;
    /** Postal address, three fields like `BuildingInfo`. Replaced the single `address` string 2026-09-26; `parseTenant` maps a legacy value onto `street`. */
    street?: string;
    postalCode?: string;
    city?: string;
    comment?: string;
    /**
     * Set by the `anonymizeTenant` callable when a referenced tenant is "deleted":
     * the name is "Borttagen hyresgäst", contact details are gone. Hidden from the
     * tenant list and every picker, still resolved by name for leases and logs.
     */
    anonymized?: true;
    createdBy: string;      // UID
    createdWhen: string;    // Stockholm 'YYYY-MM-DD hh:mm:ss'
    updatedBy?: string;
    updatedWhen?: string;
}

export interface Tenant extends TenantData {
    key: string;
}

/** Editable tenant fields. */
export interface TenantInput {
    kind: TenantKind;
    name: string;
    orgNumber?: string;
    email?: string;
    phone?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    comment?: string;
}

/** "Storgatan 1, 111 22 Stockholm" — whichever of the three parts are set; null when none are. */
export const formatTenantAddress = (tenant: Pick<TenantData, 'street' | 'postalCode' | 'city'>): string | null => {
    const cityLine = [tenant.postalCode, tenant.city].filter(Boolean).join(' ');
    const parts = [tenant.street, cityLine].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
};

/** Hyresgäster (/tenants) with the tenant open in its pane. */
export const tenantPath = (tenantKey: string): string => `/tenants?tenant=${encodeURIComponent(tenantKey)}`;
